import { Id, Observer, UserRole } from '@nextforce/api'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useState } from 'react'
import { useToast } from 'react-native-toast-notifications'
import uuid from 'react-native-uuid'
import { errorAlert } from 'shared/alerts'
import { t } from '../app/i18l'
import { useAddEditUserContext } from './context'
import { useAddUser } from './queries/add'
import { useDeleteUser } from './queries/delete'
import { useEditUser } from './queries/edit'
import keys from './queries/shared/keys'
import { AddEditUserQueryParams } from './queries/shared/types'

interface ManagementTeamActionsContextValue {
  getIsUserUpdating: (userId: string) => boolean
  onEditUser: (params: AddEditUserQueryParams) => void
  onDeleteUser: (companyId: Id, userId: string) => void
  onAddUser: (params: AddEditUserQueryParams) => void
  tempUsers: Observer[]
}

export type ManagementTeamActionsToastType = 'add' | 'update' | 'delete'
const ManagementTeamActionToastContext = createContext(
  {} as ManagementTeamActionsContextValue
)

export function ManagementTeamActionsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const toast = useToast()
  const client = useQueryClient()
  const { role } = useAddEditUserContext()
  const editUserMutation = useEditUser()
  const addUserMutation = useAddUser()
  const deleteUserMutation = useDeleteUser()

  const [userUpdatingMap, setUserUpdatingMap] = useState<{
    [userId: string]: boolean
  }>({})

  const [tempUsers, setTempUsers] = useState<Observer[]>([])

  const getIsUserUpdating = (userId: string) => {
    return userUpdatingMap[userId] || false
  }

  const createTempUser = useCallback(
    (
      firstName: string | undefined,
      lastName: string | undefined,
      userRole: UserRole | undefined
    ) => {
      const randomUserId = uuid.v4().toString()
      setTempUsers(currentTempUsers => [
        ...currentTempUsers,
        {
          id: randomUserId,
          firstName,
          lastName,
          role: userRole,
        },
      ])
      return randomUserId
    },
    []
  )

  const deleteTempUser = useCallback((userId: string) => {
    setTempUsers(currentTempUsers =>
      currentTempUsers.filter(user => user.id !== userId)
    )
  }, [])

  const setIsUserUpdating = useCallback(
    (userId: string, isUpdating: boolean) => {
      setUserUpdatingMap(currentUserUpdatingMap => ({
        ...currentUserUpdatingMap,
        [userId]: isUpdating,
      }))
    },
    []
  )

  const showToast = useCallback(
    ({
      type,
      name,
      userRole,
    }: {
      type: ManagementTeamActionsToastType
      name: string
      userRole?: UserRole
    }) => {
      const title = getToastTitle(type)
      const message = getToastMessage(type, name, userRole)
      toast.show(title, { data: { message, type } })
    },
    [toast]
  )

  const onDeleteUser = useCallback(
    async (companyId: Id, userId: string) => {
      try {
        setIsUserUpdating(userId, true)
        const key = keys.detail(companyId, userId)
        const userData = client.getQueryData<Observer>(key)
        await deleteUserMutation.mutateAsync({
          userId,
          role,
          stores: userData?.stores,
          companyId,
        })
        showToast({
          type: 'delete',
          name: `${userData?.firstName} ${userData?.lastName}`,
          userRole: userData?.role,
        })
      } catch (e) {
        const errorTitle =
          role === 'observer'
            ? t('deleteObserverErrorTitle')
            : t('deleteUserErrorTitle')
        errorAlert(e as Error, errorTitle)
      } finally {
        setIsUserUpdating(userId, false)
      }
    },
    [client, deleteUserMutation, role, setIsUserUpdating, showToast]
  )

  const onAddUser = useCallback(
    async (params: AddEditUserQueryParams) => {
      const tempUserId = createTempUser(
        params.firstName,
        params.lastName,
        params.role
      )
      setIsUserUpdating(tempUserId, true)
      try {
        const result = await addUserMutation.mutateAsync(params)
        showToast({
          type: 'add',
          name: `${result.firstName} ${result.lastName}`,
          userRole: params.role,
        })
      } catch (e) {
        errorAlert(e as Error, t('teamMemberAddErrorTitle'))
      } finally {
        deleteTempUser(tempUserId)
        setIsUserUpdating(tempUserId, false)
      }
    },
    [
      addUserMutation,
      createTempUser,
      deleteTempUser,
      setIsUserUpdating,
      showToast,
    ]
  )

  const onEditUser = useCallback(
    async (params: AddEditUserQueryParams) => {
      if (params.userId) {
        try {
          setIsUserUpdating(params.userId, true)
          const result = await editUserMutation.mutateAsync(params)
          showToast({
            type: 'update',
            name: `${result.firstName} ${result.lastName}`,
            userRole: params.role,
          })
        } catch (e) {
          errorAlert(e as Error, t('teamMemberUpdateErrorTitle'))
        } finally {
          setIsUserUpdating(params.userId, false)
        }
      }
    },
    [setIsUserUpdating, showToast, editUserMutation]
  )

  return (
    <ManagementTeamActionToastContext.Provider
      value={{
        getIsUserUpdating,
        onEditUser,
        onAddUser,
        onDeleteUser,
        tempUsers,
      }}
    >
      {children}
    </ManagementTeamActionToastContext.Provider>
  )
}

export const useManagementTeamActions = () => {
  return useContext(ManagementTeamActionToastContext)
}

const getToastTitle = (type: ManagementTeamActionsToastType) => {
  switch (type) {
    case 'add':
      return t('teamMemberAdded')
    case 'update':
      return t('teamMemberUpdated')
    case 'delete':
      return t('teamMemberDeleted')
  }
}

const getToastMessage = (
  type: ManagementTeamActionsToastType,
  name: string,
  userRole?: UserRole
) => {
  switch (type) {
    case 'add':
      return t('teamMemberAddedDescription', {
        name,
        teamName: getTeamName(userRole),
      })
    case 'update':
      return t('teamMemberUpdatedDescription', {
        name,
        teamName: getTeamName(userRole),
      })
    case 'delete':
      return t('teamMemberDeletedDescription', {
        name,
        teamName: getTeamName(userRole),
      })
  }
}

function getTeamName(userRole: UserRole | undefined) {
  switch (userRole) {
    case 'observer':
      return t('management')
    case 'guest':
      return t('guestTeam')
    case 'staff':
      return t('staffTeam')
    default:
      return ''
  }
}
